"use client";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCityTimings } from "@/redux/search/searchSlice";
import { usefetchDataFromApi } from "@/utils/api";

// SearchPill renders the desktop and mobile forms side by side and lets CSS hide
// one, so both mount and both run this hook — plus a third when the header pill is
// on screen. Sharing the in-flight promise collapses those into one request.
//
// In-flight only, deliberately: no result cache. A second stored copy of these
// hours going stale is the whole reason this feature exists, and the endpoint
// already caches server-side, so a later remount re-asking costs almost nothing.
const inFlight = new Map();

const fetchDays = (cityId) => {
  if (inFlight.has(cityId)) return inFlight.get(cityId);

  const request = (async () => {
    try {
      const res = await usefetchDataFromApi({
        url: `data/city-timings?cityId=${encodeURIComponent(cityId)}`,
        method: "GET",
      });
      return res?.success ? res.data?.data?.days ?? null : null;
    } catch {
      return null;
    } finally {
      inFlight.delete(cityId);
    }
  })();

  inFlight.set(cityId, request);
  return request;
};

// Keeps searchReducer.cityTimings in step with the selected city.
//
// Keyed on the city id rather than hooked into handleCitySelection so every route
// into a city is covered — dropdown pick, GPS detect, an SEO landing page seeding
// the store, or a restored session — instead of four call sites that drift apart.
//
// Anything other than a clean answer leaves cityTimings null, and null means "no
// restriction". A city whose hours we failed to fetch must still offer every slot:
// a loose picker costs one wasted search, a wrong-but-confident one costs the booking.
export default function useCityTimings() {
  const dispatch = useDispatch();
  const cityId = useSelector((state) => state.search.city?.id);

  useEffect(() => {
    // Clear FIRST, every time the city changes. Holding the previous city's hours
    // until the new ones land let them be applied to the wrong city: switching
    // Chennai (09-21) -> Bangalore (00-23) at 1am rolled the pickup to 9am against
    // Chennai's window, and once Bangalore's hours arrived 9am was already valid,
    // so it never rolled back. The customer silently lost the 1am slot.
    // An empty gap means "unknown" — no restriction — which is the safe reading.
    dispatch(setCityTimings(null));

    if (!cityId) return;

    let cancelled = false;
    fetchDays(cityId).then((days) => {
      if (!cancelled) dispatch(setCityTimings({ cityId, days }));
    });

    return () => {
      cancelled = true;
    };
  }, [cityId, dispatch]);
}
